import type { FieldValidation } from '@sentra/contracts';

/**
 * The `min` and `max` a date control is bounded by, derived from the field's own validation record.
 *
 * **The bounds are a courtesy; the validator is the authority.** A browser's date picker refuses
 * what its `min`/`max` exclude, which is what stops an operator scrolling to 1904 for a date of
 * birth or to next year for one; but `fieldValueSchema` is what decides whether a value is accepted,
 * here and at the server, and it is what produces the message. That ordering matters in one
 * direction: a bound TIGHTER than the validator would refuse a value the product accepts, with no
 * message and nothing to click, so where the two could disagree the bound is computed to match the
 * validator's own arithmetic rather than approximated.
 *
 * **`minYear` and `maxAgeYears` both produce a lower bound, and the later of the two wins.** The
 * validator refuses if either is breached, so the accepted set is their intersection; taking the
 * first one found would let the picker offer dates the validator then refuses.
 */

/** The average Gregorian year in milliseconds, which is the unit `fieldValueSchema` measures age in. */
const AVERAGE_YEAR_MS = 365.2425 * 24 * 60 * 60 * 1000;

/** A `YYYY-MM-DD` from an epoch instant, in UTC, which is the zone a calendar date is compared in. */
function isoDateOf(epochMs: number): string {
  return new Date(epochMs).toISOString().slice(0, 10);
}

/**
 * The earliest date `maxAgeYears` admits.
 *
 * `yearsBetween(value, today) > maxAgeYears` is the refusal, so the earliest accepted instant is
 * exactly `maxAgeYears` average years before today. It is rounded UP to the next whole day, because
 * rounding down would name a day the validator refuses and the picker would offer it.
 */
function earliestForAge(today: string, maxAgeYears: number): string {
  const todayMs = Date.parse(`${today}T00:00:00Z`);
  const earliestMs = todayMs - maxAgeYears * AVERAGE_YEAR_MS;
  const dayMs = 24 * 60 * 60 * 1000;
  return isoDateOf(Math.ceil(earliestMs / dayMs) * dayMs);
}

/**
 * Ascending order over `YYYY-MM-DD`.
 *
 * Written out rather than left to the default sort: an ISO date sorts correctly by code point, but a
 * comparator-less `sort` is the one that silently starts sorting numbers as text the day somebody
 * reuses it.
 */
function byDate(left: string, right: string): number {
  if (left < right) {
    return -1;
  }
  return left > right ? 1 : 0;
}

/** What a `<input type="date">` should be bounded by. An absent bound is an absent attribute. */
export interface DateBounds {
  readonly min?: string;
  readonly max?: string;
}

export function dateBounds(
  validation: Extract<FieldValidation, { type: 'date' }>,
  today: string,
): DateBounds {
  const lower: string[] = [];
  if (validation.notPast === true) {
    lower.push(today);
  }
  if (validation.minYear !== undefined) {
    lower.push(`${String(validation.minYear).padStart(4, '0')}-01-01`);
  }
  if (validation.maxAgeYears !== undefined) {
    lower.push(earliestForAge(today, validation.maxAgeYears));
  }

  // The LATEST of the lower bounds, because the validator refuses if any one of them is breached:
  // the accepted set is their intersection, and taking the first would offer dates it then refuses.
  const min = lower.toSorted(byDate).at(-1);
  const max = validation.notFuture === true ? today : undefined;

  return {
    ...(min === undefined ? {} : { min }),
    ...(max === undefined ? {} : { max }),
  };
}
